import React, { useState, useEffect } from 'react';
import { InverterMetricsTable } from '../components/InverterPerformance';
import { inverterPerformanceService, InverterPerformanceResponse } from '../services/inverterPerformanceService';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { MetricCard } from '../components/ui/MetricCard';

export const InverterMetrics: React.FC = () => {
  const [data, setData] = useState<InverterPerformanceResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedPlant, setSelectedPlant] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    fetchInverterData();
  }, [selectedDate, selectedPlant, statusFilter]);

  const fetchInverterData = async () => {
    try {
      setLoading(true);
      const response = await inverterPerformanceService.getInverterPerformance(
        selectedDate, 
        selectedPlant || undefined,
        statusFilter || undefined
      );
      setData(response);
    } catch (error) {
      console.error('Error fetching inverter data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = (format: 'csv' | 'pdf') => {
    if (!data) return;
    
    if (format === 'csv') {
      const csvContent = [
        ['Inverter ID', 'Specific Yield', 'Efficiency (%)', 'PR (%)', 'CUF (%)', 'Status'],
        ...data.inverters.map(inv => [
          inv.inverter_id,
          inv.specific_yield,
          inv.efficiency,
          inv.pr,
          inv.cuf,
          inv.status
        ])
      ].map(row => row.join(',')).join('\n');
      
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `inverter-metrics-${selectedDate}.csv`;
      a.click();
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Inverter Metrics - PSPB_Hindupur</h1>
        
        <div className="flex gap-4">
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="px-3 py-2 border rounded-md"
          />
          <select
            value={selectedPlant}
            onChange={(e) => setSelectedPlant(e.target.value)}
            className="px-3 py-2 border rounded-md"
          >
            <option value="">All Plants</option>
            <option value="1">PSPB_Hindupur</option>
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border rounded-md"
          >
            <option value="">All Status</option>
            <option value="generating">Generating</option>
            <option value="offline">Offline</option>
            <option value="faulty">Faulty</option>
            <option value="maintenance">Maintenance</option>
          </select>
        </div>
      </div>

      {data && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <MetricCard
              title="Total Inverters"
              value={data.summary.total_inverters}
              unit=""
              trend="neutral"
            />
            <MetricCard
              title="Avg Efficiency"
              value={data.summary.avg_efficiency}
              unit="%"
              trend={data.summary.avg_efficiency >= 90 ? 'up' : 'down'}
            />
            <MetricCard
              title="Avg PR"
              value={data.summary.avg_pr}
              unit="%"
              trend={data.summary.avg_pr >= 85 ? 'up' : 'down'}
            />
            <MetricCard
              title="Online Count"
              value={data.summary.status_counts.generating || 0}
              unit=""
              trend={data.summary.status_counts.generating > 0 ? 'up' : 'down'}
            />
          </div>

          <InverterMetricsTable 
            inverters={data.inverters} 
            onExport={handleExport}
          />
        </>
      )}
    </div>
  );
};